/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsUnshare } from "../funcs/skillsUnshare.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UnshareSkillRequest,
  UnshareSkillSecurity,
} from "../models/operations/unshareskill.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UnshareSkillMutationVariables = {
  request: UnshareSkillRequest;
  security?: UnshareSkillSecurity | undefined;
  options?: RequestOptions;
};

export type UnshareSkillMutationData = void;

export type UnshareSkillMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * unshare skills
 *
 * @remarks
 * Revoke a skill's active public share link. Repeated requests are a no-op.
 */
export function useUnshareSkillMutation(
  options?: MutationHookOptions<
    UnshareSkillMutationData,
    UnshareSkillMutationError,
    UnshareSkillMutationVariables
  >,
): UseMutationResult<
  UnshareSkillMutationData,
  UnshareSkillMutationError,
  UnshareSkillMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUnshareSkillMutation(client, options),
    ...options,
  });
}

export function mutationKeyUnshareSkill(): MutationKey {
  return ["@gram/client", "skills", "unshare"];
}

export function buildUnshareSkillMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UnshareSkillMutationVariables,
  ) => Promise<UnshareSkillMutationData>;
} {
  return {
    mutationKey: mutationKeyUnshareSkill(),
    mutationFn: function unshareSkillMutationFn({
      request,
      security,
      options,
    }): Promise<UnshareSkillMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsUnshare(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
